/**
 * Delete / restore / reset bookkeeping for a dashboard's count-panel layouts (LC Dashboards,
 * Accounting Dashboards).
 *
 * Panel layouts live in a single user setting per dashboard (`lc_dashboard_panels`,
 * `accounting_dashboard_panels`) that is overwritten in place, so before this there was
 * no way back from a reset or a workspace delete — the previous arrangement was simply
 * gone (#33552). These helpers keep the most recent displaced layout per workspace inside
 * that same blob, which makes reset undoable and lets a workspace restored from the
 * trashcan bring its panel positions back with it.
 *
 * Deliberately scoped to panel layouts rather than generalised over user_settings: no
 * new table, endpoint, or migration, and the blob stays self-describing.
 *
 * Pure and immutable — inputs are React state and are never mutated.
 */

export type PanelTrashReason = 'reset' | 'workspace-deleted' | 'copied-over';

export interface PanelTrashEntry<T> {
  items: T[];
  /** ISO timestamp, injected by the caller so this stays deterministic under test. */
  deletedAt: string;
  reason: PanelTrashReason;
  /**
   * Groups entries displaced by one action. A reset touches a single workspace, but
   * copying a colleague's configuration replaces every workspace at once and has to be
   * undoable as one step — so restore works on a batch, not on a workspace.
   */
  batchId: string;
}

/**
 * Reasons the in-page Restore action will undo. `workspace-deleted` is excluded on
 * purpose: that layout belongs to a workspace that no longer exists, and putting it
 * back would recreate the orphan the delete cleaned up. It comes back through the
 * trashcan's workspace Restore instead, which restores the cards alongside it.
 */
const RESTORABLE_IN_PAGE: PanelTrashReason[] = ['reset', 'copied-over'];

export type PanelLayoutMap<T> = { [workspaceId: string]: T[] };
export type PanelTrashMap<T> = { [workspaceId: string]: PanelTrashEntry<T> };

/**
 * How many displaced *actions* to keep — a whole-board copy is one batch however many
 * workspaces it replaced, so batches are never split by pruning. Keeps the
 * setting_value (TEXT) comfortably bounded; a single workspace layout is ~2.5KB.
 */
export const PANEL_TRASH_BATCH_LIMIT = 5;

/** Batch ids newest-first, by the newest deletedAt in each; id-ascending tie-break. */
const batchIdsNewestFirst = <T>(trash: PanelTrashMap<T>): string[] => {
  const newest = new Map<string, string>();
  Object.values(trash).forEach((entry) => {
    const prev = newest.get(entry.batchId);
    if (!prev || (entry.deletedAt || '').localeCompare(prev) > 0) {
      newest.set(entry.batchId, entry.deletedAt || '');
    }
  });
  return [...newest.keys()].sort((a, b) => {
    const byDate = (newest.get(b) || '').localeCompare(newest.get(a) || '');
    return byDate !== 0 ? byDate : a.localeCompare(b);
  });
};

/** Drop all but the `limit` most recent batches, keeping each batch whole. */
export const pruneTrash = <T>(
  trash: PanelTrashMap<T>,
  limit: number = PANEL_TRASH_BATCH_LIMIT,
): PanelTrashMap<T> => {
  const batches = batchIdsNewestFirst(trash);
  if (batches.length <= limit) return trash;
  const keep = new Set(batches.slice(0, limit));
  const kept: PanelTrashMap<T> = {};
  Object.entries(trash).forEach(([ws, entry]) => {
    if (keep.has(entry.batchId)) kept[ws] = entry;
  });
  return kept;
};

/**
 * Move one or more workspaces' saved panel layouts into the trash as a single
 * undoable batch.
 *
 * Workspaces with no saved layout are skipped — there is nothing to come back to, and
 * an empty entry would restore to the same auto-flow the reset already produces. If
 * nothing at all was displaced the maps are returned untouched (`trashed: false`), so
 * double-clicking Reset can't consume the undo. Any previous entry for a workspace is
 * replaced: this is an undo stack of actions, not a full history per workspace.
 */
export const trashPanelLayouts = <T>(
  layouts: PanelLayoutMap<T>,
  trash: PanelTrashMap<T>,
  workspaceIds: string[],
  reason: PanelTrashReason,
  deletedAt: string,
  batchId: string,
  limit: number = PANEL_TRASH_BATCH_LIMIT,
): { layouts: PanelLayoutMap<T>; trash: PanelTrashMap<T>; trashed: boolean } => {
  const displaced = workspaceIds.filter((ws) => layouts[ws]?.length);
  if (displaced.length === 0) {
    return { layouts, trash, trashed: false };
  }
  const nextLayouts = { ...layouts };
  const nextTrash = { ...trash };
  displaced.forEach((ws) => {
    nextTrash[ws] = { items: layouts[ws], deletedAt, reason, batchId };
    delete nextLayouts[ws];
  });
  return { layouts: nextLayouts, trash: pruneTrash(nextTrash, limit), trashed: true };
};

/** Single-workspace convenience wrapper: a batch of one, keyed by the workspace. */
export const trashPanelLayout = <T>(
  layouts: PanelLayoutMap<T>,
  trash: PanelTrashMap<T>,
  workspaceId: string,
  reason: PanelTrashReason,
  deletedAt: string,
  limit: number = PANEL_TRASH_BATCH_LIMIT,
): { layouts: PanelLayoutMap<T>; trash: PanelTrashMap<T>; trashed: boolean } =>
  trashPanelLayouts(
    layouts,
    trash,
    [workspaceId],
    reason,
    deletedAt,
    `${reason}:${workspaceId}:${deletedAt}`,
    limit,
  );

/** Whether there is a layout to put back for this workspace. */
export const hasTrashedPanelLayout = <T>(
  trash: PanelTrashMap<T>,
  workspaceId: string,
): boolean => !!trash[workspaceId]?.items?.length;

export interface RestorableBatch {
  batchId: string;
  reason: PanelTrashReason;
  deletedAt: string;
  workspaceIds: string[];
}

/**
 * The most recent action the in-page Restore can undo, or null if there is nothing.
 * Used both to decide whether to offer the action and to label it (one workspace vs
 * a whole-board copy).
 */
export const latestRestorableBatch = <T>(
  trash: PanelTrashMap<T>,
): RestorableBatch | null => {
  const eligible: PanelTrashMap<T> = {};
  Object.entries(trash).forEach(([ws, entry]) => {
    if (RESTORABLE_IN_PAGE.includes(entry.reason) && entry.items?.length) {
      eligible[ws] = entry;
    }
  });
  const [batchId] = batchIdsNewestFirst(eligible);
  if (!batchId) return null;
  const workspaceIds = Object.keys(eligible)
    .filter((ws) => eligible[ws].batchId === batchId)
    .sort();
  return {
    batchId,
    reason: eligible[workspaceIds[0]].reason,
    deletedAt: eligible[workspaceIds[0]].deletedAt,
    workspaceIds,
  };
};

/**
 * Put every layout displaced by one action back, consuming those trash entries.
 * Layouts saved for those workspaces since the action are overwritten — restoring is
 * "give me back what I had", not a merge.
 *
 * Undoing a whole-board copy additionally discards what the copy WROTE: the banked
 * entries are the complete pre-copy board, so anything else in the map came from the
 * colleague. Without this, a workspace they had arranged and the copier never did would
 * keep the borrowed layout after the undo.
 */
export const restorePanelBatch = <T>(
  layouts: PanelLayoutMap<T>,
  trash: PanelTrashMap<T>,
  batchId: string,
): { layouts: PanelLayoutMap<T>; trash: PanelTrashMap<T>; restored: string[] } => {
  const restored = Object.keys(trash).filter(
    (ws) => trash[ws].batchId === batchId && trash[ws].items?.length,
  );
  if (restored.length === 0) return { layouts, trash, restored: [] };
  const undoingCopy = trash[restored[0]].reason === 'copied-over';
  const nextLayouts: PanelLayoutMap<T> = undoingCopy ? {} : { ...layouts };
  const nextTrash = { ...trash };
  restored.forEach((ws) => {
    nextLayouts[ws] = trash[ws].items;
    delete nextTrash[ws];
  });
  return { layouts: nextLayouts, trash: nextTrash, restored: restored.sort() };
};

/**
 * Put a workspace's displaced layout back and consume the trash entry. A restore with
 * nothing to restore leaves both maps untouched (`restored: false`).
 */
export const restorePanelLayout = <T>(
  layouts: PanelLayoutMap<T>,
  trash: PanelTrashMap<T>,
  workspaceId: string,
): { layouts: PanelLayoutMap<T>; trash: PanelTrashMap<T>; restored: boolean } => {
  if (!hasTrashedPanelLayout(trash, workspaceId)) {
    return { layouts, trash, restored: false };
  }
  const nextTrash = { ...trash };
  delete nextTrash[workspaceId];
  return {
    layouts: { ...layouts, [workspaceId]: trash[workspaceId].items },
    trash: nextTrash,
    restored: true,
  };
};

const KNOWN_REASONS: PanelTrashReason[] = ['reset', 'workspace-deleted', 'copied-over'];

/** Coerce a persisted value into a trash map, tolerating anything an older blob holds. */
export const readPanelTrash = <T>(value: unknown): PanelTrashMap<T> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const out: PanelTrashMap<T> = {};
  Object.entries(value as Record<string, unknown>).forEach(([key, entry]) => {
    if (!entry || typeof entry !== 'object') return;
    const { items, deletedAt, reason, batchId } = entry as PanelTrashEntry<T>;
    if (!Array.isArray(items) || items.length === 0) return;
    out[key] = {
      items,
      deletedAt: typeof deletedAt === 'string' ? deletedAt : '',
      reason: KNOWN_REASONS.includes(reason) ? reason : 'reset',
      // Entries written before batching stand alone; keying the synthetic batch on the
      // workspace keeps each one independently restorable.
      batchId: typeof batchId === 'string' && batchId ? batchId : `legacy:${key}`,
    };
  });
  return out;
};

/**
 * Replace the whole board with a colleague's arrangement, banking the current one as a
 * single undoable batch.
 *
 * Whole-board by design: "copy their configuration" replaces every workspace, so a
 * workspace the source has never arranged is left with no saved layout and falls back
 * to the auto-flow — which is what that colleague sees too. The source's items are
 * cloned so the picker's data can't alias live state.
 */
export const applyCopiedPanelLayouts = <T>(
  mine: PanelLayoutMap<T>,
  trash: PanelTrashMap<T>,
  theirs: PanelLayoutMap<T>,
  deletedAt: string,
  batchId: string,
  limit: number = PANEL_TRASH_BATCH_LIMIT,
): { layouts: PanelLayoutMap<T>; trash: PanelTrashMap<T>; replaced: string[]; applied: string[] } => {
  const banked = trashPanelLayouts(
    mine,
    trash,
    Object.keys(mine),
    'copied-over',
    deletedAt,
    batchId,
    limit,
  );
  const layouts: PanelLayoutMap<T> = {};
  Object.entries(theirs).forEach(([ws, items]) => {
    if (!Array.isArray(items) || items.length === 0) return;
    layouts[ws] = items.map((item) => ({ ...item }));
  });
  return {
    layouts,
    trash: banked.trash,
    replaced: Object.keys(mine).filter((ws) => mine[ws]?.length).sort(),
    applied: Object.keys(layouts).sort(),
  };
};
